import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardHeader,
    Container,
    Grid,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
   useTheme
} from "@mui/material";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import Page from "../../components/Page";
import LoadingBackdrop from "../../components/LoadingBackdrop";
import { BASE_HOST, getAccountAPIHeader } from "../../utils/urlConst";
import { auth } from "../../firebase";

export default function CreativeSelection() {
    const [user] = useAuthState(auth);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const theme = useTheme();

    // SMS & Email weights state
    const [smsWeights, setSmsWeights] = useState({});
    const [emailWeights, setEmailWeights] = useState({});

    // State for SMS & Email data tables
    const [smsData, setSmsData] = useState([{}]);
    const [emailData, setEmailData] = useState([{}]);

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({ open: false, source: "", weights: {} });
    const [errorDialog, setErrorDialog] = useState({ open: false, message: "" });
    // Handle input changes
    const handleSmsChange = (e) => setSmsWeights({ ...smsWeights, [e.target.name]: parseFloat(e.target.value) || 0 });
    const handleEmailChange = (e) => setEmailWeights({ ...emailWeights, [e.target.name]: parseFloat(e.target.value) || 0 });

    // Fetch SMS & Email weights (last 5 entries)
    const fetchWeights = async (source, setDataFunction) => {
        setLoading(true);
        try {
            const idToken = await user.getIdToken();
            const response = await axios.get(`${BASE_HOST}/api/basic/algorithm-selection-weights?source=${source}&page=1&limit=5`, getAccountAPIHeader(idToken));
            setDataFunction(response.data.results);
        } catch (error) {
            console.error(`Error fetching ${source} weights:`, error);
        }
        setLoading(false);
    };

    // Fetch weights on component mount
    useEffect(() => {
        if (user) {
            fetchWeights("sms", setSmsData);
            fetchWeights("email", setEmailData);
        }
    }, [user]);

    // Show confirmation dialog before updating
    const showConfirmDialog = (source, weights) => {
        setConfirmDialog({ open: true, source, weights });
    };

    // Confirm & proceed with the update
    const confirmUpdate = async () => {
        setLoading(true);
        const { source, weights } = confirmDialog;

        try {
            const idToken = await user.getIdToken();
            await axios.post(`${BASE_HOST}/api/basic/algorithm-selection-weights?source=${source}`, weights, getAccountAPIHeader(idToken));
            setMessage(`${source.toUpperCase()} Weights updated successfully!`);
            fetchWeights(source, source === "sms" ? setSmsData : setEmailData);
        } catch (error) {
            setErrorDialog({
            open: true,
            message: `❌ Error updating ${source} weights: ${error.response?.data?.error || error.message}` });
        }

        // Close dialog only after update completes
        setConfirmDialog({ open: false, source: "", weights: {} });
        setLoading(false);
    };

    // Function to handle cancel action (NO REDIRECTION)
    const handleCancel = () => {
        setConfirmDialog({ open: false, source: "", weights: {} });
    };

    // Function to format timestamps for table headers
    const getTimestamps = (data) => data.map(entry => new Date(entry.timestamp).toLocaleString());

    return (
        <Page title="Creative Selection">
            {loading && <LoadingBackdrop />}
            <Container maxWidth="lg">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h4">Creative Selection Weights</Typography>
                    </Grid>

                    {/* SMS Section */}
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader title="SMS Selection Weights" />
                            <Box p={3}>
                                <Grid container spacing={2}>
                                    {Object.keys(smsData.at(-1)).map((field) => (
                                      field === "timestamp" ? null :
                                        <Grid item xs={4} key={field}>
                                            <TextField fullWidth label={field.replace("_", " ")} name={field} type="number" value={smsWeights[field]} onChange={handleSmsChange} />
                                        </Grid>
                                    ))}
                                    <Grid item xs={12}>
                                        <Button variant="contained" color="primary" onClick={() => showConfirmDialog("sms", smsWeights)}>
                                            Update SMS Weights
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Card>
                                    {/* Transposed SMS Table */}
                        <TableContainer component={Paper} sx={{ marginTop: 3 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Method</TableCell>
                                        {getTimestamps(smsData).map((timestamp, index) => (
                                            <TableCell key={index} sx={index === 0 ? { backgroundColor: "#A0D995", fontWeight: "bold" } : {}}>{timestamp}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.keys(smsData.at(-1)).map((method, rowIndex) => (
                                      method === "timestamp" ? null :
                                        <TableRow key={rowIndex}>
                                            <TableCell>{method.replace("_", " ")}</TableCell>
                                            {smsData.map((entry, colIndex) => (
                                                <TableCell key={colIndex} sx={colIndex === 0 ? { backgroundColor: "#A0D995", fontWeight: "bold" } : {}}>
                                                    {entry[method]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    {/* Email Section */}
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader title="Email Selection Weights" />
                            <Box p={3}>
                                <Grid container spacing={2}>
                                    {Object.keys(emailData.at(-1)).map((field) => (
                                      field === "timestamp" ? null :
                                        <Grid item xs={4} key={field}>
                                            <TextField fullWidth label={field.replace("_", " ")} name={field} type="number" value={emailWeights[field]} onChange={handleEmailChange} />
                                        </Grid>
                                    ))}
                                    <Grid item xs={12}>
                                        <Button variant="contained" color="primary" onClick={() => showConfirmDialog("email", emailWeights)}>
                                            Update Email Weights
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Card>
                        {/* Transposed Email Table */}
                        <TableContainer component={Paper} sx={{ marginTop: 3 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Method</TableCell>
                                        {getTimestamps(emailData).map((timestamp, index) => (
                                            <TableCell key={index} sx={index === 0 ? { backgroundColor: "#A0D995", fontWeight: "bold" } : {}}>{timestamp}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.keys(emailData.at(-1)).map((method, rowIndex) => (
                                      // skip if method === timestamp
                                        method === "timestamp" ? null :
                                        <TableRow key={rowIndex}>
                                            <TableCell>{method.replace("_", " ")}</TableCell>
                                            {emailData.map((entry, colIndex) => (
                                                <TableCell key={colIndex} sx={colIndex === 0 ? { backgroundColor: "#A0D995", fontWeight: "bold" } : {}}>
                                                    {entry[method]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    {/* Confirmation Dialog */}
                    <Dialog open={confirmDialog.open} onClose={handleCancel}>
                        <DialogTitle>⚠️ Confirm Update</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Below weights will be updated for <strong>{confirmDialog.source.toUpperCase()}</strong>.
                                Please confirm!
                            </DialogContentText>
                            <pre
                              style={{
                                  backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : "#f5f5f5",
                                  color: theme.palette.mode === "dark" ? "#E0E0E0" : "#333333",
                                  padding: "10px",
                                  borderRadius: "5px",
                                  border: `1px solid ${theme.palette.mode === "dark" ? "#444444" : "#dddddd"}`,
                                  boxShadow: theme.palette.mode === "dark" ? "0px 4px 10px rgba(255, 255, 255, 0.1)" : "0px 4px 10px rgba(0, 0, 0, 0.1)"
                              }}
                            >
                            {JSON.stringify(confirmDialog.weights, null, 2)}
                        </pre>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCancel} color="secondary">Cancel</Button>
                            <Button onClick={confirmUpdate} color="primary" variant="contained">Confirm</Button>
                        </DialogActions>
                    </Dialog>
                        <Dialog open={errorDialog.open} onClose={() => setErrorDialog({ open: false, message: "" })}>
                            <DialogTitle>⚠️ Update Failed</DialogTitle>
                            <DialogContent>
                                <DialogContentText>{errorDialog.message}</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setErrorDialog({ open: false, message: "" })} color="secondary">
                                    Close
                                </Button>
                            </DialogActions>
                    </Dialog>
                </Grid>
            </Container>
        </Page>
    );
}